import React, { useState, useEffect, useRef } from 'react';
import { LogOut, Sparkles, Maximize, Minimize, Upload } from 'lucide-react';
import { useRbac } from '../context';
import { logoutUser } from '../services/apiclient';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';
import { updateMyProfile, uploadFile } from '../services/usersapi';
import { toast } from 'react-hot-toast';

/** Temporary: hide AI Mode in the top bar; set true to show again */
const SHOW_AI_MODE_HEADER_BUTTON = false;

const TopHeader: React.FC = () => {
  const { user, hasPermission, setUser } = useRbac();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDialog, setShowDialog] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if fullscreen is active
  useEffect(() => {
    const checkFullscreen = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);
    document.addEventListener('MSFullscreenChange', checkFullscreen);

    // Initial check
    checkFullscreen();

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
      document.removeEventListener('MSFullscreenChange', checkFullscreen);
    };
  }, []);

  const handleLogout = async () => {
    await logoutUser();
    setShowDialog(false);
    navigate('/login');
  };

  const handleAiModeClick = () => {
    navigate('/ai-mode');
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if ((document.documentElement as any).webkitRequestFullscreen) {
          await (document.documentElement as any).webkitRequestFullscreen();
        } else if ((document.documentElement as any).mozRequestFullScreen) {
          await (document.documentElement as any).mozRequestFullScreen();
        } else if ((document.documentElement as any).msRequestFullscreen) {
          await (document.documentElement as any).msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      // Upload the file
      const folderPath = `users/${user?.id}/profile`;
      const uploadResponse = await uploadFile(file, folderPath);
      
      // Update profile with new photo URL
      const response = await updateMyProfile({ 
        profilePhotoUrl: uploadResponse.fileUrl 
      });

      // Update user context
      if (response.data?.user && setUser) {
        setUser({
          ...user!,
          profilePhotoUrl: response.data.user.profilePhotoUrl
        });
      }

      toast.success('Profile photo updated successfully!');
      setIsPhotoViewerOpen(false);
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isAiModeActive = location.pathname === '/ai-mode';
  const canAccessAiMode = hasPermission('aiMode');

  if (!user) {
    return null;
  }

  return (
    <div className="w-full bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between sticky top-0 z-30">
      {/* Left side - AI Mode */}
      <div className="flex items-center">
        {SHOW_AI_MODE_HEADER_BUTTON && canAccessAiMode && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAiModeClick}
            className={`flex items-center gap-2 text-sm font-medium transition-colors duration-200 ${
              isAiModeActive
                ? 'bg-[#FFF5F2] text-[#E66A4E] border border-[#E66A4E]'
                : 'text-gray-700 hover:bg-[#EC6E51]/90 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">AI Mode</span>
          </Button>
        )}
      </div>

      {/* Right side - Notification, Fullscreen, Username, Logout */}
      <div className="flex items-center gap-3">
        {/* Notification Bell */}
        <NotificationBell />

        {/* Fullscreen Button */}
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-600 hover:text-[#EE7D63] hover:bg-gray-50"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <Minimize className="w-4 h-4" />
          ) : (
            <Maximize className="w-4 h-4" />
          )}
        </Button>

        {/* Username */}
        <div className="flex items-center gap-2">
          {user?.profilePhotoUrl ? (
            <img
              src={user.profilePhotoUrl}
              alt={user?.username || 'Profile'}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setIsPhotoViewerOpen(true)}
              onError={(e) => {
                // Fallback to initial letter if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-8 h-8 bg-[#EE7D63] rounded-full flex items-center justify-center flex-shrink-0 ${user?.profilePhotoUrl ? 'hidden' : ''}`}
          >
            <span className="text-white font-medium text-sm">
              {user?.username?.[0]?.toUpperCase() ?? ''}
            </span>
          </div>
          <span className="text-sm font-medium text-gray-700 hidden sm:block">
            {user?.username}
          </span>
        </div>

        {/* Logout Button */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-600 hover:text-[#EE7D63] hover:bg-gray-50"
            onClick={() => setShowDialog(true)}
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="ml-2 hidden sm:inline">Logout</span>
          </Button>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-800">
                Confirm Logout
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600 mt-2">
              Are you sure you want to log out?
            </p>
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="outline"
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="px-4 py-2 rounded-lg bg-[#EE7D63] text-white hover:bg-[#E66A4E]"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Profile Photo Viewer Dialog */}
      <Dialog open={isPhotoViewerOpen} onOpenChange={setIsPhotoViewerOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-800">
              {user?.username}'s Profile Photo
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 gap-4">
            {user?.profilePhotoUrl ? (
              <img 
                src={user.profilePhotoUrl} 
                alt={user.username}
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
              />
            ) : (
              <div className="text-gray-500 p-8">No profile photo available</div>
            )}
            
            {/* Edit Photo Button */}
            <div className="flex gap-3">
              <Button
                onClick={handleFileSelect}
                disabled={isUploading}
                className="bg-[#EE7D63] hover:bg-[#E66A4E] text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : user?.profilePhotoUrl ? 'Change Photo' : 'Upload Photo'}
              </Button>
            </div>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TopHeader;

